<template>
  <div class="pattern-picker">
    <div class="pattern-picker__selected-background">
      <div
        class="pattern-picker__preview customization-drawer__theme-preview"
        :class="[
          `editor-theme-thumbnail--${pageTheme.pattern}`,
          `editor-theme--${pageTheme.name}-${pageTheme.variation}`,
          !selectedPatternLabel && 'pattern-picker__preview--no-background'
        ]"
      />
      <div class="pattern-picker__preview-label">
        {{ $t(selectedPatternLabel) || $t('No Background') }}
      </div>
      <NebulaButton
        v-if="pageTheme.pattern && pageTheme.pattern !== 'none'"
        class="pattern-picker__clear-button"
        :aria-label="$t('Clear Background')"
        icon-left="trash"
        size="l"
        type="flat"
        @click="clearBackground"
      />
    </div>
    <div class="pattern-picker__apply-to-all-wrapper">
      <NebulaButton
        aria-describedby="pattern-picker__apply-to-all-aria-description"
        size="s"
        type="ghost"
        :text="$t('Apply to All')"
        @click="applyBackgroundToAll"
      />
      <span
        id="pattern-picker__apply-to-all-aria-description"
        class="pattern-picker__aria-description"
      >
        {{ draftType === 'board'
          ? $t('Apply the current page background to all pages')
          : $t('Apply the current slide background to all slides') }}
      </span>
    </div>
    <div
      class="comet-tabs pattern-picker__tabs"
      :aria-label="$t('Filter backgrounds')"
      role="group"
    >
      <button
        class="comet-tab__label pattern-picker__background-tab"
        :class="backgroundTab === 'images' && 'comet-tab__label--selected'"
        @click="backgroundTab = 'images'"
      >
        <span class="comet-tab__label-text">{{ $t('Images') }}</span>
        <span
          class="comet-screenreader-only"
          v-if="backgroundTab === 'images'"
        >
          {{ $t('(selected)') }}
        </span>
      </button>
      <button
        class="comet-tab__label pattern-picker__background-tab"
        :class="backgroundTab === 'patterns' && 'comet-tab__label--selected'"
        @click="backgroundTab = 'patterns'"
      >
        <span class="comet-tab__label-text">{{ $t('Patterns') }}</span>
        <span
          class="comet-screenreader-only"
          v-if="backgroundTab === 'patterns'"
        >
          {{ $t('(selected)') }}
        </span>
      </button>
    </div>
    <div class="pattern-picker__patterns">
      <button
        v-for="pattern in filteredPatterns"
        :key="pattern.id"
        class="pattern-picker__pattern"
        aria-describedby="pattern-picker__apply-background-aria-description"
        @click="selectPattern(pattern.id)"
      >
        <span class="pattern-picker__pattern-name">
          {{ $t(pattern.name) }}
        </span>
        <div
          class="pattern-picker__pattern-thumbnail editor-theme customization-drawer__theme-preview"
          :class="[
            `editor-theme-thumbnail--${pattern.id}`,
            `editor-theme--${pageTheme.name}-${pageTheme.variation}`,
          ]"
        />
      </button>
      <span
        id="pattern-picker__apply-background-aria-description"
        class="pattern-picker__aria-description"
      >
        {{ $t('Press Enter to apply this background') }}
      </span>
    </div>
  </div>
</template>

<script>
import { NebulaButton } from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';
import { patterns } from '@/lib/constants/patterns.json';

export default {
  name: 'PatternPicker',
  components: {
    NebulaButton,
  },
  data() {
    return {
      backgroundTab: 'images',
    };
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
    ]),
    filteredPatterns() {
      return patterns.filter((pattern) => (
        (this.backgroundTab === 'images' && pattern.isOpaque)
        || (this.backgroundTab === 'patterns' && !pattern.isOpaque)
      ));
    },
    pageTheme() {
      return get(this, 'currentPage.options.theme', {});
    },
    selectedPatternLabel() {
      const selectedPattern = patterns.find((pattern) => pattern.id === this.pageTheme.pattern);
      return selectedPattern && selectedPattern.name;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_ALL_PAGE_OPTIONS,
      types.UPDATE_PAGE_OPTIONS,
    ]),
    applyBackgroundToAll() {
      this[types.UPDATE_ALL_PAGE_OPTIONS]({
        options: {
          theme: {
            pattern: this.pageTheme.pattern || 'none',
          },
        },
      });
    },
    clearBackground() {
      this[types.UPDATE_PAGE_OPTIONS]({
        options: {
          ...this.currentPage.options,
          theme: {
            ...this.pageTheme,
            pattern: 'none',
          },
        },
      });
    },
    selectPattern(patternId) {
      this[types.UPDATE_PAGE_OPTIONS]({
        options: {
          ...this.currentPage.options,
          theme: {
            ...this.pageTheme,
            pattern: patternId,
          },
        },
      });
    },
  },
};
</script>

<style lang="stylus">
.pattern-picker {
  display: flex;
  flex-direction: column;
  padding-top: $nebula-space-2x;

  &__patterns {
    flex-grow: 1;
    padding: $nebula-space-2x;
  }

  &__selected-background {
    align-items: center;
    background-color: $nebula-color-platform-neutral-300;
    border-radius: $nebula-border-radius-badge-default;
    display: flex;
    flex-shrink: 0;
    font-weight: bold;
    margin: 0 $nebula-space-2x;
  }

  &__preview {
    background-color: #414858; // default background color
    flex-shrink: 0;
    height: 42px;
    margin: $nebula-space-1x;
    width: 56px;

    &.pattern-picker__preview--no-background {
      background: linear-gradient(143deg, $nebula-color-white 48.5%, $nebula-color-error 49%,
        $nebula-color-error 51%, $nebula-color-white 51.5%);
    }
  }

  &__preview-label {
    flex-grow: 1;
  }

  &__clear-button.nebula-button {
    align-self: stretch;
    background-color: $nebula-color-platform-neutral-300;
    border-radius: 0 $nebula-border-radius-badge-default $nebula-border-radius-badge-default 0;
    margin: 0;

    svg {
      fill: inherit;
    }

    &:hover, &:focus {
      background-color: $nebula-color-platform-neutral-400;
    }

    &:active {
      background-color: $nebula-color-platform-neutral-500;

      svg {
        fill: inherit;
      }
    }
  }

  &__apply-to-all-wrapper {
    padding: $nebula-space-2x $nebula-space-2x 0;

    .nebula-button {
      margin: 0;
    }
  }

  &__aria-description {
    display: none;
  }

  &__tabs {
    flex-shrink: 0;
    margin: 0 $nebula-space-2x;
  }

  &__background-tab {
    border: none;
    border-radius: 0;
    background: none;
  }

  &__pattern {
    background: none;
    border: none;
    border-radius: $nebula-border-radius-badge-default;
    box-shadow: $nebula-shadow-200;
    display: block;
    margin: 0 0 $nebula-space-2x;
    overflow: hidden;
    padding: 0;
    text-align: start;
    width: 100%;
    transition: box-shadow $nebula-transition-default, transform $nebula-transition-default;

    &:hover,
    &:active,
    &:focus {
      box-shadow: $nebula-shadow-300;
      transform: translateY(-2px);
    }
  }

  &__pattern-name {
    display: block;
    font-size: $nebula-font-size-body-2;
    margin: $nebula-space-1x;
  }

  &__pattern-thumbnail {
    height: 42px;
    width: 100%;
  }

  html[dir="rtl"] & {
    &__apply-to-all-wrapper .nebula-button {
      margin: 0;
    }

    &__clear-button.nebula-button {
      border-radius: $nebula-border-radius-badge-default 0 0 $nebula-border-radius-badge-default;
    }
  }
}
</style>
